// src/lib/push.js
// FCM token registration + foreground push handling for HomeIQa.
// Tokens are stored under agents/{uid}.fcmTokens[] so each user's
// devices receive only their own pushes.
import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { db, getMessagingIfSupported, FIREBASE_VAPID_PUBLIC_KEY } from '../firebase';
import { playChime, showBrowserNotification } from './notify';

const SW_PATH = '/firebase-messaging-sw.js';
let cachedToken = null;
let foregroundUnsub = null;

async function getServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(SW_PATH);
  } catch (e) {
    console.error('SW registration failed', e);
    return null;
  }
}

// Register this device for lock-screen push notifications for the given user.
// Returns the FCM token on success, throws on failure.
export async function registerForPush(uid) {
  if (!uid) throw new Error('uid required');
  if (!FIREBASE_VAPID_PUBLIC_KEY) {
    throw new Error('VAPID key not configured. Generate one in Firebase Console → Project Settings → Cloud Messaging → Web Push certificates, then set REACT_APP_FIREBASE_VAPID_KEY in .env and redeploy.');
  }
  const messaging = await getMessagingIfSupported();
  if (!messaging) throw new Error('Push notifications are not supported in this browser. On iPhone, install HomeIQa to your home screen first.');

  // Permission first.
  if (typeof Notification === 'undefined') throw new Error('Notifications not supported.');
  if (Notification.permission === 'denied') throw new Error('Notifications are blocked by the browser. Enable HomeIQa notifications in browser settings, then try again.');
  if (Notification.permission !== 'granted') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') throw new Error('Permission not granted.');
  }

  const swReg = await getServiceWorkerRegistration();
  if (!swReg) throw new Error('Could not register service worker.');

  const token = await getToken(messaging, {
    vapidKey: FIREBASE_VAPID_PUBLIC_KEY,
    serviceWorkerRegistration: swReg
  });
  if (!token) throw new Error('Could not get a push token.');

  cachedToken = token;
  // Persist to the user's agent doc — array union dedupes automatically.
  await updateDoc(doc(db, 'agents', uid), {
    fcmTokens: arrayUnion(token)
  }).catch(async () => {
    // If fcmTokens field doesn't exist yet, the update still works because
    // arrayUnion creates it. The catch is here only for unrelated failures.
  });
  setupForegroundHandler(messaging);
  return token;
}

// Remove THIS device's token from the user's fcmTokens[] (does not affect
// other devices).
export async function unregisterFromPush(uid) {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return;
  let token = cachedToken;
  if (!token) {
    try {
      token = await getToken(messaging, { vapidKey: FIREBASE_VAPID_PUBLIC_KEY }).catch(() => null);
    } catch {}
  }
  if (token) {
    try { await deleteToken(messaging); } catch {}
    if (uid) {
      try {
        await updateDoc(doc(db, 'agents', uid), { fcmTokens: arrayRemove(token) });
      } catch {}
    }
  }
  cachedToken = null;
  if (foregroundUnsub) { foregroundUnsub(); foregroundUnsub = null; }
}

// Returns true if THIS browser has a token already saved on the user's doc.
export async function isPushRegistered(uid) {
  if (!uid) return false;
  const messaging = await getMessagingIfSupported();
  if (!messaging) return false;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
  try {
    const swReg = await getServiceWorkerRegistration();
    if (!swReg) return false;
    const token = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_PUBLIC_KEY,
      serviceWorkerRegistration: swReg
    }).catch(() => null);
    if (!token) return false;
    const snap = await getDoc(doc(db, 'agents', uid));
    if (!snap.exists()) return false;
    const tokens = snap.data().fcmTokens || [];
    cachedToken = token;
    return tokens.includes(token);
  } catch { return false; }
}

// Foreground messages — the SW handler doesn't fire when the tab is open,
// so we still want a chime + tab-level notification when something arrives.
function setupForegroundHandler(messaging) {
  if (foregroundUnsub) return;
  foregroundUnsub = onMessage(messaging, payload => {
    const data = payload.data || {};
    const notif = payload.notification || {};
    const kind = data.kind || null; // 'chat' | 'email' | 'sms' | null
    playChime(kind);
    showBrowserNotification(
      notif.title || data.title || 'HomeIQa',
      notif.body || data.body || '',
      () => {
        try { window.focus(); } catch {}
        const target = data.url;
        if (target) {
          try { window.location.href = target; } catch {}
        }
      }
    );
  });
}
